// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { BILLING_BREADCRUMB_ROOT } from '@/billing/components/billing-nav';
import { PerCurrencySections } from '@/billing/components/currency-section-accordion';
import { AggregateGantt } from '@/billing/components/lifecycle-aggregate-gantt';
import { LifecycleBenchmarkIndicator } from '@/billing/components/lifecycle-benchmark-indicator';
import { ClusterBrace } from '@/billing/components/lifecycle-cluster-brace';
import type { LifecycleSortColumn } from '@/billing/components/lifecycle-table';
import { LifecycleTable } from '@/billing/components/lifecycle-table';
import { useInvoiceLifecycleListing } from '@/billing/hooks/use-invoice-lifecycle-listing';
import { useInvoiceLifecycleSummary } from '@/billing/hooks/use-invoice-lifecycle-summary';
import { SEGMENT_BENCHMARKS } from '@/billing/lifecycle/benchmarks';
import { formatDays } from '@/billing/lifecycle/date-helpers';
import {
  ADS_ADC_CLUSTERS,
  SEGMENTS,
  type SegmentDefinition,
} from '@/billing/lifecycle/segments';
import {
  type LifecycleRow,
  type SegmentSummary,
  apiRowToLifecycleRow,
  apiSummaryToSegmentSummaries,
} from '@/billing/lifecycle/transforms';
import { EmptyState } from '@/components/empty-state';
import { PageContent } from '@/components/page-content';
import { SectionBreadcrumbs } from '@/components/section-breadcrumbs';
import { PrimaryMetric, SummaryCard } from '@/components/summary-card';
import { SupportSnackbar } from '@/components/support-snackbar';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import {
  InfoOutlined,
  SearchRounded,
} from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { SortDirection, invert, sortDirectionSchema } from '@/constants/sort';
import type { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { useCustomers } from '@/customer/hooks/use-customers';
import { CustomerFilter } from '@/filters/customer-filter';
import { ProjectFilter } from '@/filters/project-filter';
import { useSparseProjectList } from '@/project/hooks/use-sparse-project-list';
import { formatCurrency } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import { searchTermSchema } from '@/utilities/search-term';
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Route definition
// ---------------------------------------------------------------------------

const searchSchema = z.object({
  customerIds: z.array(z.string()).optional(),
  projectIds: z.array(z.string()).optional(),
  query: searchTermSchema,
  page: z.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: sortDirectionSchema.optional(),
});

export const Route = createFileRoute('/_app/billing/lifecycle')({
  component: BillingLifecycleRoute,
  validateSearch: searchSchema,
});

const LIFECYCLE_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;
const EM_DASH = '—';

const DEFAULT_SORT_COLUMN: LifecycleSortColumn = 'totalCycle';
const DEFAULT_SORT_DIRECTION: SortDirection = 'desc';

// One currency's flattened segment summaries — the lifecycle report stacks
// one cluster-card + Gantt section per invoice currency.
interface CurrencyLifecycleSummary {
  isoCode: IsoCurrencyCode;
  summaries: SegmentSummary[];
}

function BillingLifecycleRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useCurrentUser();

  const activeCustomerIds = useMemo(
    () => search.customerIds ?? [],
    [search.customerIds]
  );
  const activeProjectIds = useMemo(
    () => search.projectIds ?? [],
    [search.projectIds]
  );
  const searchQuery = search.query ?? '';
  const currentPage = search.page ?? 1;
  const sortColumn: LifecycleSortColumn =
    (search.sortBy as LifecycleSortColumn | undefined) ?? DEFAULT_SORT_COLUMN;
  const sortDirection: SortDirection =
    search.sortOrder ?? DEFAULT_SORT_DIRECTION;

  // Local input state drives the TextField responsively; navigate (which
  // triggers the listing refetch) is debounced so typing doesn't issue a
  // request per keystroke. Same pattern as billing.aging.tsx.
  const [searchInput, setSearchInput] = useState(searchQuery);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    if (searchTimeoutRef.current === null) {
      setSearchInput(searchQuery);
    }
  }, [searchQuery]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current !== null) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // CustomerFilter / ProjectFilter emit selected UUIDs (their internal id),
  // but the lifecycle endpoints filter by nano keys. Map at the
  // query-building boundary, mirroring the aging report's approach.
  const { customers } = useCustomers();
  const { projects } = useSparseProjectList();

  const customerKeyById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of customers) {
      map.set(c.id, c.key);
    }
    return map;
  }, [customers]);
  const projectKeyById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of projects) {
      map.set(p.id, p.key);
    }
    return map;
  }, [projects]);

  const filterCustomerKeys = useMemo(() => {
    if (activeCustomerIds.length === 0) return undefined;
    const keys: string[] = [];
    for (const id of activeCustomerIds) {
      const key = customerKeyById.get(id);
      if (key !== undefined) keys.push(key);
    }
    return keys.length > 0 ? keys : undefined;
  }, [activeCustomerIds, customerKeyById]);
  const filterProjectKeys = useMemo(() => {
    if (activeProjectIds.length === 0) return undefined;
    const keys: string[] = [];
    for (const id of activeProjectIds) {
      const key = projectKeyById.get(id);
      if (key !== undefined) keys.push(key);
    }
    return keys.length > 0 ? keys : undefined;
  }, [activeProjectIds, projectKeyById]);

  const { summary, isError: isSummaryError } = useInvoiceLifecycleSummary({
    customerKeys: filterCustomerKeys,
    projectKeys: filterProjectKeys,
  });
  const {
    rows,
    totalCount,
    isError: isListingError,
  } = useInvoiceLifecycleListing({
    customerKeys: filterCustomerKeys,
    projectKeys: filterProjectKeys,
    search: searchQuery.length > 0 ? searchQuery : undefined,
    page: currentPage,
    pageSize: LIFECYCLE_PAGE_SIZE,
    sortBy: sortColumn,
    sortOrder: sortDirection,
  });

  const pageRows = useMemo<LifecycleRow[]>(() => {
    if (rows === null) return [];
    return rows.map(apiRowToLifecycleRow);
  }, [rows]);

  const currencySummaries = useMemo<CurrencyLifecycleSummary[] | null>(() => {
    if (!summary) return null;
    return summary.byCurrency.map((bucket) => ({
      isoCode: bucket.isoCode,
      summaries: apiSummaryToSegmentSummaries(bucket),
    }));
  }, [summary]);

  const [isLoadErrorDismissed, setIsLoadErrorDismissed] = useState(false);
  const isLoadErrorOpen =
    (isSummaryError || isListingError) && !isLoadErrorDismissed;

  const isListingLoading = rows === null;
  const showEmptyState = totalCount !== null && totalCount === 0;

  const hasActiveFilters =
    activeCustomerIds.length > 0 ||
    activeProjectIds.length > 0 ||
    searchQuery.length > 0;

  const handleChangeCustomer = (customerIds: string[]) => {
    void navigate({
      search: {
        ...search,
        customerIds: customerIds.length === 0 ? undefined : customerIds,
        page: undefined,
      },
    });
  };

  const handleChangeProject = (projectIds: string[]) => {
    void navigate({
      search: {
        ...search,
        projectIds: projectIds.length === 0 ? undefined : projectIds,
        page: undefined,
      },
    });
  };

  const handleSearch = (value: string) => {
    setSearchInput(value);
    if (searchTimeoutRef.current !== null) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchTimeoutRef.current = null;
      const latestSearch = searchRef.current;
      void navigate({
        search: {
          ...latestSearch,
          query: value.length === 0 ? undefined : value,
          page: undefined,
        },
      });
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleClearFilters = () => {
    if (searchTimeoutRef.current !== null) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setSearchInput('');
    void navigate({
      search: {
        ...search,
        customerIds: undefined,
        projectIds: undefined,
        query: undefined,
        page: undefined,
      },
    });
  };

  const handleSort = (column: LifecycleSortColumn) => {
    let nextOrder: SortDirection;
    if (sortColumn === column) {
      nextOrder = invert(sortDirection);
    } else {
      const textColumns: LifecycleSortColumn[] = [
        'customer',
        'project',
        'invoiceNumber',
      ];
      nextOrder = textColumns.includes(column) ? 'asc' : 'desc';
    }
    void navigate({
      search: {
        ...search,
        sortBy:
          column === DEFAULT_SORT_COLUMN && nextOrder === DEFAULT_SORT_DIRECTION
            ? undefined
            : column,
        sortOrder:
          column === DEFAULT_SORT_COLUMN && nextOrder === DEFAULT_SORT_DIRECTION
            ? undefined
            : nextOrder,
        page: undefined,
      },
    });
  };

  const handlePageChange = (page: number) => {
    void navigate({
      search: {
        ...search,
        page: page === 1 ? undefined : page,
      },
    });
  };

  return (
    <PageContent>
      <Stack spacing={2}>
        <Stack>
          <SectionBreadcrumbs
            items={[BILLING_BREADCRUMB_ROOT, { label: 'Billing lifecycle' }]}
          />
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
          >
            <Typography variant="h5">Billing lifecycle</Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Last 365 days
              </Typography>
              <Tooltip
                title="Includes invoices with an invoice date in the last 365 days"
                arrow
                placement="bottom"
              >
                <InfoOutlined
                  fontSize="small"
                  sx={{ color: 'text.secondary' }}
                />
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>

        {currencySummaries === null ? (
          <CurrencyLifecycleSection
            currencyCode={null}
            summaries={null}
            isLoading
            weightedAverageCostOfCapital={
              user.organization.weightedAverageCostOfCapital
            }
          />
        ) : (
          <PerCurrencySections
            buckets={currencySummaries}
            renderBucket={(cs) => (
              <CurrencyLifecycleSection
                currencyCode={cs.isoCode}
                summaries={cs.summaries}
                isLoading={false}
                weightedAverageCostOfCapital={
                  user.organization.weightedAverageCostOfCapital
                }
              />
            )}
          />
        )}

        <Stack direction="row" alignItems="center" spacing={2}>
          <CustomerFilter
            value={activeCustomerIds}
            onChange={handleChangeCustomer}
          />
          <ProjectFilter
            value={activeProjectIds}
            onChange={handleChangeProject}
          />
          {hasActiveFilters && (
            <Button size="small" onClick={handleClearFilters}>
              Clear filters
            </Button>
          )}

          <Box flex={1} />

          <TextField
            size="small"
            placeholder="Search"
            value={searchInput}
            inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
            onChange={(e) => handleSearch(e.target.value)}
            sx={{ width: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded
                    fontSize="small"
                    sx={{ color: 'neutral.400' }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        {showEmptyState ? (
          <EmptyState
            title="No billing lifecycle data for this period"
            message="Invoices that have been opened or paid within the selected period will appear here"
          />
        ) : (
          <LifecycleTable
            data={pageRows}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            isLoading={isListingLoading}
          />
        )}

        {totalCount !== null && totalCount > 0 && (
          <TablePaginationFooter
            pagination={{
              page: currentPage,
              pageSize: LIFECYCLE_PAGE_SIZE,
              onPageChange: handlePageChange,
            }}
            totalCount={totalCount}
            entityOptions={{ singular: 'invoice' }}
            downloadURL={
              new URL(
                '/v1/export/billing-lifecycle',
                import.meta.env.VITE_API_URL as string
              )
            }
            downloadLabel="Export lifecycle data"
          />
        )}
      </Stack>
      <SupportSnackbar
        isOpen={isLoadErrorOpen}
        onClose={() => setIsLoadErrorDismissed(true)}
        message="An error occurred while loading the billing lifecycle report."
      />
    </PageContent>
  );
}

// Renders the ADS / ADC cluster cards plus the aggregate Gantt for a single
// invoice currency. The report stacks one of these per currency; a null
// currencyCode/summaries pair is the loading state (skeleton cards, no Gantt).
function CurrencyLifecycleSection(props: {
  currencyCode: IsoCurrencyCode | null;
  summaries: SegmentSummary[] | null;
  isLoading: boolean;
  weightedAverageCostOfCapital: string;
}) {
  const { currencyCode, summaries, isLoading, weightedAverageCostOfCapital } =
    props;
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={3} sx={{ width: '100%' }}>
        {ADS_ADC_CLUSTERS.map((cluster) => {
          const clusterSegments = cluster.segmentKeys
            .map((key) => SEGMENTS.find((s) => s.key === key))
            .filter((s): s is SegmentDefinition => s !== undefined);

          return (
            <Box
              key={cluster.id}
              sx={{ flex: cluster.segmentKeys.length, minWidth: 0 }}
            >
              <ClusterBrace
                label={cluster.label}
                fullLabel={cluster.fullLabel}
                description={cluster.description}
              />
              <Grid container spacing={2}>
                {clusterSegments.map((segment) => (
                  <Grid
                    item
                    xs={12}
                    sm={12 / clusterSegments.length}
                    key={segment.key}
                  >
                    <ClusterSummaryCard
                      segment={segment}
                      summary={
                        summaries?.find((s) => s.key === segment.key) ?? null
                      }
                      isLoading={isLoading}
                      currencyCode={currencyCode}
                      weightedAverageCostOfCapital={
                        weightedAverageCostOfCapital
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}
      </Stack>

      {summaries !== null && currencyCode !== null && (
        <AggregateGantt summaries={summaries} currencyCode={currencyCode} />
      )}
    </Stack>
  );
}

// A single summary card inside an ADS / ADC cluster. Pulled out of the
// main component to keep the route-level JSX scannable.
export function ClusterSummaryCard(props: {
  segment: SegmentDefinition;
  summary: SegmentSummary | null;
  isLoading: boolean;
  currencyCode: IsoCurrencyCode | null;
  weightedAverageCostOfCapital: string;
}) {
  const {
    segment,
    summary,
    isLoading,
    currencyCode: isoCodeProp,
    weightedAverageCostOfCapital,
  } = props;
  const avgDays = summary?.avgDays ?? null;
  // PeriodCost / annualCost are null when no invoices observed the segment
  // — there is no input to compound, so the card renders an em-dash
  // instead of a fabricated "$0/yr".
  const periodCost = summary?.totalCarryingCost ?? null;
  const annualCost = summary?.annualizedCarryingCost ?? null;
  const count = summary?.invoiceCount ?? 0;
  const currencyCode = isoCodeProp ?? undefined;
  const annualCostLabel =
    annualCost === null
      ? EM_DASH
      : annualCost.isZero()
        ? `${formatCurrency(0, { abbreviate: true, digits: 0, currencyCode })}/yr carrying cost`
        : `${formatCurrency(annualCost.abs().toNumber(), { abbreviate: true, digits: 0, currencyCode })}/yr carrying ${annualCost.lessThan(0) ? 'gain' : 'cost'}`;
  const weightedAverageCostOfCapitalPercent =
    Number(weightedAverageCostOfCapital) * 100;
  const tooltipTitle =
    periodCost === null || isoCodeProp === null
      ? `No invoices observed this segment over the last 365 days`
      : `${formatCurrencyValue({ amount: periodCost.abs(), isoCode: isoCodeProp })} ${periodCost.lessThan(0) ? 'carrying gain' : 'carrying cost'} based on ${pluralize(count, { singular: 'invoice' })} over the last 365 days at ${weightedAverageCostOfCapitalPercent.toLocaleString(undefined, { maximumFractionDigits: 2 })}% weighted average cost of capital, compounded and annualized`;
  // `totalCycle` doesn't render as a cluster card on this page, so the
  // lookup is always defined for the six ADS / ADC segments.
  const benchmark =
    segment.key === 'totalCycle' ? null : SEGMENT_BENCHMARKS[segment.key];
  return (
    <SummaryCard>
      <PrimaryMetric
        label={segment.label}
        value={avgDays !== null ? formatDays(avgDays) : EM_DASH}
        color={segment.color}
        tooltip={segment.tooltip}
        isLoading={isLoading}
      />
      {benchmark !== null && (
        <LifecycleBenchmarkIndicator
          value={avgDays}
          benchmark={benchmark}
          isLoading={isLoading}
        />
      )}
      {isLoading ? (
        <Skeleton variant="text" width="60%" sx={{ ml: 'auto' }} />
      ) : (
        <Box sx={{ textAlign: 'right' }}>
          <Tooltip title={tooltipTitle} arrow placement="bottom">
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{
                display: 'inline-block',
                fontStyle: 'italic',
                cursor: 'pointer',
              }}
            >
              {annualCostLabel}
            </Typography>
          </Tooltip>
        </Box>
      )}
    </SummaryCard>
  );
}
